import React, { FC } from 'react';

import { File as CustomFile } from '../../../generated/graphql';

import useGetAttachmentActions from '../../../hooks/attachments/useGetAttachmentActions';
import { AttachmentMode, AttachmentType } from '../../../models/attachment';

import { MessageCommon } from '../../../models/message';

import {
  getCanDeleteAttachment,
  getCanDownloadAttachment,
} from '../../../utils/permissions/messageAttachments';

import AttachmentChip from './AttachmentChip';
import AttachmentLabel from './AttachmentLabel';
import AttachmentRibbon from './AttachmentRibbon';

import { handleFileRemove } from './helpers';
import * as Styled from './styled';

export type Props = {
  attachment: AttachmentType;
  message?: MessageCommon;
  mode: AttachmentMode;
  requestFileRemove?: (key: CustomFile['key']) => void;
};

/**
 * Component that renders single attachment
 *
 * @param props                   Props passed to the component
 * @param props.attachment        File that is attached
 * @param props.message           The message itself
 * @param props.mode              In which mode to render the component
 * @param props.requestFileRemove Callback for removing file from the drafts array or message
 * @returns                       The component itself
 */
const Attachment: FC<Props> = ({
  attachment,
  message,
  mode,
  requestFileRemove,
}) => {
  const { actionDownload, actionPreview } = useGetAttachmentActions(
    attachment,
    message?.id,
  );

  /**
   * Whether user has permission to delete/download attachment
   */
  const canDeleteFile = mode === 'compose' || getCanDeleteAttachment(message);
  const canDownloadFile = getCanDownloadAttachment(message);
  /**
   * Remove file from draft or call delete mutation.
   * Depends on attachment status (draft or existing)
   *
   */
  const handleButtonClick = () => {
    handleFileRemove(attachment, canDeleteFile, requestFileRemove);
  };

  return (
    <Styled.Wrapper>
      <AttachmentChip
        attachment={attachment}
        canDeleteFile={canDeleteFile}
        isDisabled={message?.isArchived}
        mode={mode}
        requestFileRemove={handleButtonClick}
      >
        <AttachmentLabel
          actionDownload={actionDownload}
          actionPreview={actionPreview}
          attachment={attachment}
          canDownloadFile={canDownloadFile}
          mode={mode}
        />
      </AttachmentChip>
      <AttachmentRibbon
        actionDownload={actionDownload}
        actionPreview={actionPreview}
        canDownloadFile={canDownloadFile}
        mode={mode}
      />
    </Styled.Wrapper>
  );
};

export default Attachment;
